import { useState } from "react";
import { Button, Checkbox, NoticeBox, ProgressBar, StatCard, Tags } from "../ui";
import { IssueList } from "./IssueDetails";
import { APP_LOGOS, appLabel, sectionLabel } from "./reportAnalysisModel";
import { count, periodLabel } from "./format";
import { errorText } from "./reportAnalysisText";

const APP_VARIANT = { ready: "success", warning: "warning", error: "danger" };
const STATUS_VARIANT = { ready: "success", readyWithWarnings: "warning", requiresCorrection: "danger" };
const FILE_VARIANT = { verified: "success", rejected: "danger" };

// One line of a file's or application's row accounting (backend counts only).
export function rowsText(accounting, text, lang, period) {
  const p = text.preview2;
  return accounting.mode === "M"
    ? p.rowsAssignedLine(count(accounting.sourceRows, lang), count(accounting.includedRows, lang), count(accounting.excludedRows, lang), periodLabel(period, lang))
    : p.rowsLine(count(accounting.sourceRows, lang), count(accounting.includedRows, lang), count(accounting.excludedRows, lang));
}

export function RequestProgress({ text, phase, progress, label }) {
  return (
    <div className="report-analysis-progress" role="status" aria-live="polite">
      <strong>{label || text.phases[phase]}</strong>
      {typeof progress === "number" && progress < 1
        ? <ProgressBar value={progress * 100} label={text.phases.uploading} valueText={`${Math.round(progress * 100)}%`} />
        : <span className="report-analysis-muted">{text.processing}</span>}
    </div>
  );
}

// One application's check results: files, blocking errors, warnings (detailed), status.
function AppChecks({ item, text, lang, context }) {
  const r = text.review;
  const warnings = item.warnings.length + item.periodWarnings.length;
  const rowsKnown = typeof item.sourceRows === "number";
  const statusLabel = item.status === "error" ? text.flow.overall.requiresCorrection : item.status === "warning" ? text.flow.overall.readyWithWarnings : text.flow.overall.ready;
  return (
    <details className={`report-review-app is-${item.status}`} open={item.status !== "ready"} data-testid={`checks-app-${item.app}`}>
      <summary>
        {APP_LOGOS[item.app] && <img src={APP_LOGOS[item.app]} alt="" className="report-review-app__logo" />}
        <span className="report-review-app__title">
          <strong>{appLabel(item.app, lang)}</strong>
          <span className="report-analysis-muted">
            {[r.filesCount(item.files.length, count(item.files.length, lang)), rowsKnown ? r.rows(item.sourceRows, count(item.sourceRows, lang)) : null, item.errors.length ? r.errorsCount(item.errors.length, count(item.errors.length, lang)) : null, warnings ? r.warningsCount(warnings, count(warnings, lang)) : null].filter(Boolean).join(" · ")}
          </span>
        </span>
        <Tags variant={APP_VARIANT[item.status]}>{statusLabel}</Tags>
      </summary>
      <div className="report-review-app__body">
        {item.files.length > 0 && (
          <section aria-label={text.flow.filesChecked}>
            <h5>{text.flow.filesChecked}</h5>
            <ul className="report-checks-files">
              {item.files.map((file, index) => (
                <li key={`${file.fileName}-${index}`}>
                  <bdi className="report-analysis-filename">{file.fileName || "—"}</bdi>
                  <span className="report-analysis-muted">
                    {[sectionLabel(item.app, file.section, lang), file.accounting ? rowsText(file.accounting, text, lang, context.period) : typeof file.sourceRows === "number" ? r.rows(file.sourceRows, count(file.sourceRows, lang)) : null, (file.sourcePeriods || []).filter((entry) => entry.month).map((entry) => periodLabel(entry, lang)).join("، ") || null].filter(Boolean).join(" · ")}
                  </span>
                  {file.status && <Tags variant={FILE_VARIANT[file.status] || "warning"} size="sm">{text.validationStatus[file.status] || file.status}</Tags>}
                </li>
              ))}
            </ul>
          </section>
        )}
        {item.errors.length > 0 && (
          <section aria-label={r.errorsHeading}>
            <h5>{r.errorsHeading}</h5>
            <IssueList items={item.errors} kind="error" text={text} lang={lang} context={context} />
          </section>
        )}
        {warnings > 0 && (
          <section aria-label={r.warningsHeading}>
            <h5>{r.warningsHeading}</h5>
            <IssueList items={[...item.periodWarnings, ...item.warnings]} kind="warning" text={text} lang={lang} context={context} />
          </section>
        )}
        {!item.errors.length && !warnings && <p className="report-analysis-muted">{text.flow.noIssues}</p>}
      </div>
    </details>
  );
}

// Step 3 - Checks: the server's file checks on the page (or the problems found before sending).
// Blocking errors stop here; confirmable warnings need an explicit acknowledgement, bound to the
// server's confirmation token; nothing is saved in this step.
export default function ChecksStep({ text, lang, state, review, busy, onBack, onRetry, onContinue, onAcknowledge }) {
  const f = text.flow;
  const { validation, precheck, error } = state;
  // Ticks belong to one confirmation token: a new token (the earlier one expired) needs new ticks.
  const [ticks, setTicks] = useState({ token: null, general: false, period: false, daily: false, branches: false });
  const token = validation?.confirmation?.token || null;
  const current = ticks.token === token ? ticks : { token, general: false, period: false, daily: false, branches: false };
  const tick = (field) => (event) => setTicks({ ...current, [field]: Boolean(event?.target ? event.target.checked : event) });

  const checked = Boolean(validation) || precheck;
  const apps = review.apps.filter((item) => item.files.length || item.errors.length || item.warnings.length);
  const blockingCount = review.totals.errors;
  const blocking = precheck || review.rejected || blockingCount > 0;
  const hasPeriod = review.hasPeriodAck;
  const hasBranches = review.crossStoreRows !== null;
  const hasDaily = review.dailyExcludedRows !== null;
  const needsAck = !blocking && review.needsConfirmation;
  const canAck = needsAck && Boolean(token) && current.general && (!hasPeriod || current.period) && (!hasDaily || current.daily) && (!hasBranches || current.branches);
  const overall = blocking ? "requiresCorrection" : review.totals.warnings || needsAck ? "readyWithWarnings" : "ready";
  const requestFailed = error && error.step === "validate" && !busy;
  const context = { storeName: review.context.storeName, period: review.context.period };

  return (
    <section className="report-analysis-step" data-testid="step-validate" aria-label={f.checksTitle}>
      <header className="report-analysis-step__head report-analysis-step__head--row">
        <div>
          <h3>{f.checksTitle}</h3>
          <p className="report-analysis-muted">{f.checksHint}</p>
        </div>
        {checked && !busy && <span data-testid="checks-status"><Tags variant={STATUS_VARIANT[overall]}>{f.overall[overall]}</Tags></span>}
      </header>

      {busy && <RequestProgress text={text} phase={state.phase} progress={state.progress} label={f.checking} />}

      {requestFailed && (
        <NoticeBox variant="danger" title={f.requestFailed} bordered>
          <div role="alert" data-testid="checks-failure">
            <p>{errorText(text, error)}</p>
            <p className="report-analysis-muted">{f.checksNeverSave}</p>
          </div>
        </NoticeBox>
      )}
      {error && !requestFailed && error.notice && <NoticeBox variant="warning" size="sm"><span role="alert">{errorText(text, error)}</span></NoticeBox>}

      {checked && !busy && (
        <>
          {precheck && <NoticeBox variant="info" size="sm"><span data-testid="checks-precheck">{f.precheckNotice}</span></NoticeBox>}
          <div className="report-analysis-figures" data-testid="checks-summary">
            <StatCard label={f.summary.appsChecked} value={count(review.totals.apps, lang)} />
            <StatCard label={f.summary.filesChecked} value={count(review.totals.files, lang)} />
            <StatCard label={f.summary.rowsChecked} value={typeof review.totals.sourceRows === "number" ? count(review.totals.sourceRows, lang) : text.notAvailable} className={typeof review.totals.sourceRows === "number" ? "" : "is-unavailable"} />
            <StatCard label={f.summary.appsReady} value={count(review.totals.ready, lang)} />
            <StatCard label={f.summary.appsWarnings} value={count(review.totals.withWarnings, lang)} />
            <StatCard label={f.summary.appsErrors} value={count(review.totals.withErrors, lang)} />
          </div>

          {blocking && (
            <NoticeBox variant="danger" title={f.blockingTitle} bordered>
              <div role="alert" data-testid="checks-blocking">
                <p>{f.blockingHint(Math.max(blockingCount, 1), count(Math.max(blockingCount, 1), lang))}</p>
                <ul className="report-checks-blocking">
                  {apps.flatMap((item) => item.errors.map((entry, index) => {
                    const label = text.issues[entry.code];
                    return <li key={`${item.app}-${index}`}><strong>{appLabel(item.app, lang)}</strong>{entry.fileName ? <> — <bdi>{entry.fileName}</bdi></> : null}: {typeof label === "function" ? label(entry.count) : label || entry.code}</li>;
                  }))}
                  {review.general.map((entry, index) => {
                    const label = text.issues[entry.code];
                    return <li key={`general-${index}`}>{typeof label === "function" ? label(entry.count) : label || entry.code}</li>;
                  })}
                </ul>
              </div>
            </NoticeBox>
          )}
          {!blocking && !review.totals.warnings && !needsAck && <NoticeBox variant="success" size="sm">{f.allClear}</NoticeBox>}

          <div className="report-analysis-grid">
            {apps.map((item) => <AppChecks key={item.app} item={item} text={text} lang={lang} context={context} />)}
          </div>

          {needsAck && (
            <section className="report-checks-ack" aria-label={f.ackTitle} data-testid="checks-acknowledgement">
              <h4>{f.ackTitle}</h4>
              <p className="report-analysis-muted">{f.ackHint}</p>
              {hasPeriod && <Checkbox checked={current.period} onChange={tick("period")} label={text.flow.ackPeriodCheck(periodLabel(review.context.period, lang))} />}
              {hasDaily && <Checkbox checked={current.daily} onChange={tick("daily")} label={text.flow.ackDailyCheck(periodLabel(review.context.period, lang), count(review.dailyExcludedRows, lang))} />}
              {hasBranches && <Checkbox checked={current.branches} onChange={tick("branches")} label={text.flow.ackBranchesCheck(count(review.crossStoreRows, lang), review.context.storeName || "—")} />}
              <Checkbox checked={current.general} onChange={tick("general")} label={hasBranches ? text.flow.ackCheckMismatch : text.flow.ackCheck} />
            </section>
          )}
        </>
      )}

      <div className="report-analysis-actions">
        <Button appearance="soft" onClick={onBack} disabled={state.phase === "committing"}>{f.backToFiles}</Button>
        {requestFailed && <Button variant="primary" appearance="soft" onClick={onRetry}>{f.retryCheck}</Button>}
        {checked && !busy && (needsAck ? (
          <Button variant="warning" appearance="filled" disabled={!canAck} onClick={() => onAcknowledge(token, review.ackCodes)}>{f.acknowledgeContinue}</Button>
        ) : (
          <Button variant="primary" appearance="filled" disabled={blocking} onClick={onContinue}>{f.continuePreview}</Button>
        ))}
      </div>
    </section>
  );
}
